import { Button, Divider, TextField, Typography } from "@mui/material";
import { Box, Grid } from "@mui/system";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useBrandProperties } from "../brandPropertiesHook/useBrandProperties";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import { useSelector } from "react-redux";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { isValidParam } from "../utilitiesOfBrandConfig/helperOfBrandConfig";

const BrandPropertiesForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useSelector(selectUserData);
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const brand_shared_uid = searchParams.get("brandSharedUid") as any;
  const brandId = searchParams.get("brandId") as any;
  //const step = searchParams.get("step") as any;
  const [updatePropLoader, setUpdatePropLoader] = useState(false);

  const bkInterfaceSharedUid = searchParams.get("bkInterfaceSharedUid");
  const isBkInterfaceValid = isValidParam(bkInterfaceSharedUid);

  const { formattedBrandProperties } = useBrandProperties();
  const [inputProperties, setInputProperties] = useState<any>(() =>
    Array.isArray(formattedBrandProperties) &&
    formattedBrandProperties.length > 0
      ? [...formattedBrandProperties]
      : [],
  );

  // this is for check the initial value with current value of dynamic input
  const [initialBrandProperties] = useState<any>(() =>
    Array.isArray(formattedBrandProperties) &&
    formattedBrandProperties.length > 0
      ? JSON.parse(JSON.stringify(formattedBrandProperties))
      : [],
  );

  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: string;
  }>({});

  // State to track touched fields
  const [touchedFields, setTouchedFields] = useState<{
    [key: number]: boolean;
  }>({});

  // Check user permissions
  const hasBrandConfigEditPermission = CheckPermissions(
    userData?.data?.products?.partners,
    "data_intregrations",
    "configure_new_brand",
  );

  // Check if any mandatory field is empty
  const hasEmptyMandatoryFields = inputProperties.some((item: any) => {
    const isRequired =
      item.requirementType === "required" ||
      item.requirementType === "conditional_requirement";
    return isRequired && (!item.value || item.value.trim() === "");
  });

  // This form is shared between the Brand Configuration Report module
  // (operations/brand_configuration_report/brand_properties) and the Brands
  // module (operations/brands/edit_properties) — only the Brands origin gets
  // the new behavior below; the Report module's original behavior is
  // untouched.
  const isFromBrandsModule = location.pathname.startsWith(
    "/operations/brands/edit_properties",
  );
  const backButtonLabel = isFromBrandsModule
    ? "Back to Brands"
    : "Back to configuration report";

  // Function to go back to the previous page
  const backToPreviousPage = (resetPage: boolean = false) => {
    const currentParams = new URLSearchParams(location.search);
    if (isFromBrandsModule) {
      // Drop the temporary Edit Properties params so only Brands' own
      // tab/filter state (already preserved in the URL) is restored.
      currentParams.delete("brandSharedUid");
      currentParams.delete("bkInterfaceSharedUid");
      currentParams.delete("brandId");
      currentParams.delete("step");
      if (resetPage) currentParams.set("page", "0"); // Reset to first page on back
      navigate(`/operations/brands?${currentParams.toString()}`);
      return;
    }
    if (resetPage) currentParams.set("page", "0"); // Reset to first page on back
    navigate(
      `/operations/brand_configuration_report?${currentParams.toString()}`,
    );
  };

  // Handle input change
  const handleInputChange = (index: number, newValue: string) => {
    setInputProperties((prevProperties: any) =>
      prevProperties.map((item: any, i: any) =>
        i === index ? { ...item, value: newValue } : item,
      ),
    );

    // Validate field on change if it has been touched
    if (touchedFields[index]) {
      const item = inputProperties[index];
      const error = validateField(item, newValue);
      setValidationErrors((prev) => ({
        ...prev,
        [index]: error,
      }));
    }
  };

  const validateField = (item: any, value: string): string => {
    const isRequired =
      item.requirementType === "required" ||
      item.requirementType === "conditional_requirement";

    // Check if field is required and empty
    if (isRequired && (!value || value.trim() === "")) {
      return `${item.label} is required`;
    }
    if (/\s{2,}/.test(value)) {
      return "Double spaces are not allowed.";
    }
    return "";
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const errors: { [key: number]: string } = {};
    let isValid = true;

    inputProperties.forEach((item: any, index: number) => {
      const error = validateField(item, item.value);
      if (error) {
        errors[index] = error;
        isValid = false;
      }
    });

    setValidationErrors(errors);

    // Mark all fields as touched
    const allTouched = inputProperties.reduce(
      (acc: any, _: any, index: number) => {
        acc[index] = true;
        return acc;
      },
      {},
    );
    setTouchedFields(allTouched);

    return isValid;
  };

  // Handle field blur
  const handleFieldBlur = (index: number) => {
    setTouchedFields((prev) => ({
      ...prev,
      [index]: true,
    }));

    // Validate on blur
    const item = inputProperties[index];
    const error = validateField(item, item.value);
    setValidationErrors((prev) => ({
      ...prev,
      [index]: error,
    }));
  };

  // Update placeholder value for pattern type
  function updatePlaceholderValue(
    config: any,
    placeholderKey: any,
    newValue: any,
  ) {
    config.value = newValue;

    // Update placeholder to match
    const placeholderObj = JSON.parse(config.placeholder);
    placeholderObj[placeholderKey] = newValue;
    config.placeholder = JSON.stringify(placeholderObj);

    return config.placeholder;
  }

  // Get changed properties - only compare fields that had initial values
  const getChangedProperties = () => {
    const changedProperties = inputProperties.reduce(
      (acc: any[], item: any, index: number) => {
        const initialItem = initialBrandProperties[index];

        if (!initialItem) return acc;

        // Normalize values for comparison
        const normalizeValue = (val: any) => {
          if (val === null || val === undefined || val === "") return null;
          return String(val).trim();
        };

        const currentValue = normalizeValue(item.value);
        const initialValue = normalizeValue(initialItem.value);

        // Only include fields that:
        // 1. Had a value initially (not null/empty)
        // 2. AND the value has changed
        if (initialValue !== null && currentValue !== initialValue) {
          if (item.key_type === "pattern") {
            acc.push({
              property_target: item.property_target,
              value: updatePlaceholderValue(
                item,
                item.placeholderKey,
                item.value,
              ),
              key_name: item.key_name,
            });
          } else {
            acc.push({
              property_target: item.property_target,
              value: item.value,
              key_name: item.key_name,
            });
          }
        }

        return acc;
      },
      [],
    );

    return changedProperties;
  };

  // Get new properties - fields that were empty initially but now have values
  const getNewProperties = () => {
    const newProperties = inputProperties.reduce(
      (acc: any[], item: any, index: number) => {
        const initialItem = initialBrandProperties[index];

        if (!initialItem) return acc;

        // Normalize values
        const normalizeValue = (val: any) => {
          if (val === null || val === undefined || val === "") return null;
          return String(val).trim();
        };

        const currentValue = normalizeValue(item.value);
        const initialValue = normalizeValue(initialItem.value);

        // Only include fields that:
        // 1. Were empty initially (null/empty)
        // 2. AND now have a value
        if (initialValue === null && currentValue !== null) {
          if (item.key_type === "pattern") {
            acc.push({
              property_target: item.property_target,
              value: updatePlaceholderValue(
                item,
                item.placeholderKey,
                item.value,
              ),
              key_name: item.key_name,
            });
          } else {
            acc.push({
              property_target: item.property_target,
              value: item.value,
              key_name: item.key_name,
            });
          }
        }

        return acc;
      },
      [],
    );

    return newProperties;
  };

  // Update brand properties
  const updateBrandProperties = async () => {
    if (!validateAllFields()) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please fix all validation errors before proceeding.",
          type: "error",
        }),
      );
      return;
    }

    const changedFields = getChangedProperties();
    const newFields = getNewProperties();

    // Merge both changed and new fields
    const allFields = [...changedFields, ...newFields];

    if (allFields.length > 0) {
      setUpdatePropLoader(true);
      try {
        const response =
          await BrandsEndPointLogic.updateBrandProperties(
            oauthTokenHeader,
            { properties: allFields },
            brand_shared_uid,
          );

        if (response?.status !== 200) {
          dispatch(
            setDisplayErrorSnackBar({
              message: "Something went wrong; please try again later..",
              type: "error",
            }),
          );
          return;
        }
      } catch (e: any) {
        const errorMessage =
          e?.message ||
          e?.parsedText ||
          e?.response?.data?.message ||
          "Failed to update properties. Please try again.";
        dispatch(
          setDisplayErrorSnackBar({ message: errorMessage, type: "error" }),
        );
        return;
      } finally {
        setUpdatePropLoader(false);
      }
    }

    await processBrandConfiguration();
  };

  const processBrandConfiguration = async () => {
    const res =
      await BrandsEndPointLogic.postBrandConfigurationProcess(
        oauthTokenHeader,
        { brand_id: brandId, override_configuration: true },
      );

    if (res?.status === 200) {
      backToPreviousPage(true);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            res?.data?.message ||
            "Brand Integration Process has been successfully initiated.",
          type: "success",
        }),
      );
    } else {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to process brand integration.",
          type: "error",
        }),
      );
    }
  };

  if (inputProperties.length === 0) {
    return (
      <>
        <Box display="flex" justifyContent="center" p={4}>
          <Typography variant="body1" color="textSecondary">
            No additional fields need to be filled in.
          </Typography>
        </Box>
        <Box justifyContent={"flex-end"} display={"flex"} mb={1}>
          <Button
            disabled={updatePropLoader}
            variant="text"
            color="secondary"
            startIcon={<ArrowBackIosIcon style={{ fontSize: "15px" }} />}
            sx={{ mr: 2 }}
            onClick={() => backToPreviousPage()}
          >
            {backButtonLabel}
          </Button>

          <Button
            disabled={
              !hasBrandConfigEditPermission ||
              updatePropLoader ||
              !isBkInterfaceValid
            }
            variant="outlined"
            color="secondary"
            endIcon={<ArrowForwardIosIcon />}
            onClick={processBrandConfiguration}
          >
            Next
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box>
        {inputProperties?.length > 0 &&
          inputProperties.map((item: any, index: number) => (
            <Grid container mb={2} key={item.key_name}>
              <Grid size={6}>
                <TextField
                  disabled={updatePropLoader}
                  size="medium"
                  color="secondary"
                  fullWidth
                  label={item.label}
                  value={item.value ? item.value : ""}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onBlur={() => handleFieldBlur(index)}
                  variant="outlined"
                  required={
                    item.requirementType === "required" ||
                    item.requirementType === "conditional_requirement"
                  }
                  error={touchedFields[index] && !!validationErrors[index]}
                  helperText={
                    touchedFields[index] && validationErrors[index]
                      ? validationErrors[index]
                      : item.helperText
                  }
                />
              </Grid>
            </Grid>
          ))}
      </Box>
      <Typography variant="body2" mt={2}>
        Fields marked with * are mandatory.
      </Typography>
      <Divider sx={{ my: 3 }} />

      <Box justifyContent={"flex-end"} display={"flex"} mb={1}>
        <Button
          disabled={updatePropLoader}
          variant="text"
          color="secondary"
          startIcon={<ArrowBackIosIcon style={{ fontSize: "15px" }} />}
          sx={{ mr: 2 }}
          onClick={() => backToPreviousPage()}
        >
          {backButtonLabel}
        </Button>

        <Button
          data-tracker="633"
          disabled={
            !hasBrandConfigEditPermission ||
            updatePropLoader ||
            hasEmptyMandatoryFields
          }
          variant="outlined"
          color="secondary"
          endIcon={<ArrowForwardIosIcon />}
          onClick={updateBrandProperties}
        >
          Save & Run
        </Button>
      </Box>
    </>
  );
};

export default BrandPropertiesForm;
